/**
 * STORY SO FAR: why it is news now, then every event oldest first, with its
 * date, who it involved, where it was read and whether it holds. What a
 * reporter gets straight before writing. Built in `boardroom/story.py`.
 */
import type { BoardroomStory, BoardroomWhyNow } from '../../../types';
import { VerdictBadge } from '../ItemCard';
import { CARD } from './tableLayout';
import { heardLabel } from './painTimeline';

/** "up 340% on a year ago", "down 20%", or null. Pure. */
export function trendWords(change: number | null | undefined): string | null {
  if (change == null || !Number.isFinite(change)) return null;
  const pct = Math.round(Math.abs(change));
  if (pct < 5) return 'about level with a year ago';
  return `${change > 0 ? 'up' : 'down'} ${pct}% on a year ago`;
}

export function WhyNow({ why, onOpen }: { why: BoardroomWhyNow; onOpen: (id: number) => void }) {
  const trend = why.trend;
  const words = trendWords(trend?.change_pct);
  if (!trend && !why.latest) return null;
  return (
    <section className={`${CARD} p-3`} data-testid="why-now">
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Why now</h3>
      {trend && (
        <p className="text-sm text-gray-800 dark:text-gray-100">
          Searches for <strong className="font-medium">“{trend.keyword}”</strong>
          {words ? ` are ${words}` : ' are being tracked'}.
          {trend.rising.length > 0 && (
            <span className="text-gray-600 dark:text-gray-300"> Rising: {trend.rising.slice(0, 4).join(' · ')}.</span>
          )}
        </p>
      )}
      {why.latest && (
        <p className="mt-1 text-sm text-gray-800 dark:text-gray-100">
          Latest:{' '}
          <button type="button" onClick={() => onOpen(why.latest!.id)} className="hover:underline">
            {why.latest.title}
          </button>{' '}
          <span className="text-xs text-gray-500 dark:text-gray-400">({heardLabel(why.latest.happened_at)})</span>
        </p>
      )}
    </section>
  );
}

export default function StoryReport({ data, onOpen }: { data: BoardroomStory; onOpen: (id: number) => void }) {
  const { rows, totals } = data;
  return (
    <div className="flex flex-col gap-3" data-testid="story-report">
      <WhyNow why={data.why_now} onOpen={onOpen} />
      <p className="text-xs text-gray-400">
        {totals.events} event{totals.events === 1 ? '' : 's'} · {totals.dated} dated · {totals.checked} checked
      </p>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Nothing on the timeline yet. The chronicler adds events on the next dig.
        </p>
      ) : (
        <ol className={`${CARD} relative flex flex-col gap-0 p-3`}>
          {rows.map(e => (
            <li
              key={e.id}
              className="grid grid-cols-[6.5rem_1fr] gap-3 border-l-2 border-purple-200 py-2 pl-3 dark:border-purple-800"
            >
              <div className="text-xs tabular-nums text-gray-500 dark:text-gray-400">
                {e.happened_at ? heardLabel(e.happened_at) : <span className="italic">date unknown</span>}
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onOpen(e.id)}
                    className="text-left text-sm font-medium text-gray-900 hover:underline dark:text-gray-100"
                  >
                    {e.title}
                  </button>
                  {e.verdict && <VerdictBadge verdict={e.verdict} />}
                </div>
                {e.body && <p className="text-xs text-gray-600 dark:text-gray-300">{e.body}</p>}
                <div className="mt-0.5 flex flex-wrap gap-2 text-[11px] text-gray-500 dark:text-gray-400">
                  {e.people.map(p => (
                    <button key={p.id} type="button" onClick={() => onOpen(p.id)} className="hover:underline">
                      {p.title}
                    </button>
                  ))}
                  {e.source && (
                    <a
                      href={e.source.url}
                      target="_blank"
                      rel="noreferrer"
                      title={e.source.quote ? `“${e.source.quote}”` : e.source.url}
                      className="text-indigo-600 hover:underline dark:text-indigo-300"
                    >
                      {e.source.url.replace(/^https?:\/\//, '').split('/')[0]}
                    </a>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
